import { format, isSameDay } from "date-fns";

import { TaskSource } from "@fartherfinance/frontend/api/Tasks/Types";
import { TaskResourceMaps } from "@fartherfinance/frontend/api/TaskWorkflows/hooks/useGetTaskResources";
import { TasksFilterQueryParamsV4 } from "@fartherfinance/frontend/api/TaskWorkflows/requests/getTasks";
import {
  AdvisorId,
  ClientId,
  ServiceTeamId,
} from "@fartherfinance/frontend/api/Types";

import { mapTaskSourceToLabel } from "../../Tasks/common";

const handleFilterDateRange = (timestamps: [string, string]) => {
  const [afterTs, beforeTs] = timestamps;
  const afterDate = new Date(afterTs);
  const beforeDate = new Date(beforeTs);

  const formatedDateAfter = format(afterDate, "MM/dd/yyyy");
  const formatedDateBefore = format(beforeDate, "MM/dd/yyyy");

  const isSameDate = isSameDay(afterDate, beforeDate);

  return {
    isSameDate,
    formatedDateAfter,
    formatedDateBefore,
  };
};

type TasksFilterQueryParamsUnion = Exclude<
  | {
      [K in keyof TasksFilterQueryParamsV4]: {
        key: K;
        value: Exclude<TasksFilterQueryParamsV4[K], undefined>;
      };
    }[keyof TasksFilterQueryParamsV4]
  | {
      key: "filterByCreatedAfter&filterByCreatedBefore";
      value: [string, string];
    }
  | {
      key: "filterByUpdatedAfter&filterByUpdatedBefore";
      value: [string, string];
    }
  | {
      key: "filterByDueDateAfter&filterByDueDateBefore";
      value: [string, string];
    },
  undefined
>;

export const determineFilterChipLabel = (
  filter: TasksFilterQueryParamsUnion,
  taskResources: TaskResourceMaps | undefined
): string => {
  const {
    advisorsMap,
    clientsMap,
    serviceTeamsMap,
    taskTypesMap,
    taskStatusesMap,
    taskPrioritiesMap,
  } = taskResources ?? {};

  switch (filter.key) {
    case "filterByTaskType":
      const taskType = taskTypesMap?.get(filter.value[0]);

      return filter.value.length > 1
        ? `${filter.value.length} task types`
        : `Type: ${taskType?.displayName ?? "Unknown"}`;

    case "filterByTaskStatus":
      const taskStatus = taskStatusesMap?.get(filter.value[0]);

      return filter.value.length > 1
        ? `${filter.value.length} statuses`
        : `Status: ${taskStatus?.displayValue ?? "Unknown"}`;

    case "filterByTaskPriority":
      const taskPriority = taskPrioritiesMap?.get(filter.value[0]);

      return filter.value.length > 1
        ? `${filter.value.length} priority levels`
        : `Priority: ${taskPriority?.displayValue ?? "Unknown"}`;

    case "filterByCustodian":
      return filter.value.length > 1
        ? `${filter.value.length} custodians`
        : `Custodian: ${filter.value[0]}`;

    case "filterByIsSystemGenerated":
      return `Generation type: ${
        filter.value ? "System-generated" : "Manually-created"
      }`;

    case "filterByTaskSource":
      return filter.value.length > 1
        ? `${filter.value.length} generation types`
        : `Generation type: ${mapTaskSourceToLabel(
            filter.value[0] as TaskSource
          )}`;

    case "filterByPersonAssignee":
      const hasUnassigned = filter.value.includes("null");

      const assignee = advisorsMap?.get(filter.value[0] as AdvisorId);
      const assigneeName = hasUnassigned
        ? "Service Team"
        : assignee
        ? `${assignee.name.first} ${assignee.name.last}`
        : "Unknown assignee";

      return filter.value.length > 1
        ? `${filter.value.length} assignees`
        : `Assignee: ${assigneeName}`;

    case "filterByAdvisor":
      const advisor = advisorsMap?.get(filter.value[0] as AdvisorId);
      const advisorName = advisor
        ? `${advisor.name.first} ${advisor.name.last}`
        : "Unknown advisor";

      return filter.value.length > 1
        ? `${filter.value.length} advisors`
        : `Advisor : ${advisorName}`;

    case "filterByServiceTeam":
      // Special handling for the "null" value which means "No service team"
      const hasNullServiceTeam = filter.value.includes("null");

      if (hasNullServiceTeam && filter.value.length === 1) {
        return "Service team: None";
      }

      const serviceTeam = serviceTeamsMap?.get(
        filter.value[0] as ServiceTeamId
      );
      const serviceTeamName = serviceTeam
        ? serviceTeam.name
        : "Unknown service team";

      return filter.value.length > 1
        ? `${filter.value.length} service teams`
        : `Service team: ${serviceTeamName}`;

    case "filterByClient":
      const client = clientsMap?.get(filter.value[0] as ClientId);
      const clientName = client
        ? `${client.name.first} ${client.name.last}`
        : "Unknown client";

      return filter.value.length > 1
        ? `${filter.value.length} clients`
        : `Client: ${clientName}`;

    case "filterByCreatedAfter":
      const createdAfter = format(new Date(filter.value), "MM/dd/yyyy");
      return `Created since: ${createdAfter}`;

    case "filterByCreatedBefore":
      const createdBefore = format(new Date(filter.value), "MM/dd/yyyy");
      return `Created before: ${createdBefore}`;

    case "filterByCreatedAfter&filterByCreatedBefore":
      const {
        isSameDate: isSameDateCreated,
        formatedDateAfter: createdBetweenAfter,
        formatedDateBefore: createdBetweenBefore,
      } = handleFilterDateRange(filter.value);

      return isSameDateCreated
        ? `Created on: ${createdBetweenAfter}`
        : `Created between: ${createdBetweenAfter}-${createdBetweenBefore}`;

    case "filterByUpdatedAfter":
      const updatedAfter = format(new Date(filter.value), "MM/dd/yyyy");
      return `Updated since: ${updatedAfter}`;

    case "filterByUpdatedBefore":
      const updatedBefore = format(new Date(filter.value), "MM/dd/yyyy");
      return `Updated before: ${updatedBefore}`;

    case "filterByUpdatedAfter&filterByUpdatedBefore":
      const {
        isSameDate: isSameDateUpdated,
        formatedDateAfter: updatedBetweenAfter,
        formatedDateBefore: udpatedBetweenBefore,
      } = handleFilterDateRange(filter.value);

      return isSameDateUpdated
        ? `Updated on: ${updatedBetweenAfter}`
        : `Updated between: ${updatedBetweenAfter}-${udpatedBetweenBefore}`;

    case "filterByDueDateAfter":
      const dueAfter = format(new Date(filter.value), "MM/dd/yyyy");
      return `Due after: ${dueAfter}`;

    case "filterByDueDateBefore":
      const dueBefore = format(new Date(filter.value), "MM/dd/yyyy");
      return `Due before: ${dueBefore}`;

    case "filterByDueDateAfter&filterByDueDateBefore":
      const {
        isSameDate: isSameDateDue,
        formatedDateAfter: dueBetweenAfter,
        formatedDateBefore: dueBetweenBefore,
      } = handleFilterDateRange(filter.value);

      return isSameDateDue
        ? `Due on: ${dueBetweenAfter}`
        : `Due between: ${dueBetweenAfter}-${dueBetweenBefore}`;

    default:
      return "Unknown filter";
  }
};
